import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Crown, X, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (d) => {
  const dt = new Date(String(d).length <= 10 ? `${d}T12:00:00` : d);
  return isNaN(dt) ? '' : dt.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

// Dashboard banner: hives whose latest inspection found no queen
export const QueenlessAlerts = ({ token }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(() => {
    axios.get(`${API}/queen-alerts/queenless`, { headers }).then(res => setData(res.data)).catch(() => setData(null));
  }, [token]);

  useEffect(() => {
    load();
    window.addEventListener('refreshData', load);
    return () => window.removeEventListener('refreshData', load);
  }, [load]);

  const dismiss = async (a) => {
    setData(prev => ({ ...prev, alerts: prev.alerts.filter(x => x.hive_id !== a.hive_id) }));
    try {
      await axios.post(`${API}/queen-alerts/queenless/${a.hive_id}/dismiss`, { inspection_id: a.inspection_id }, { headers });
      toast.success(`Alert dismissed for Hive ${a.hive_number}`);
    } catch { toast.error('Could not dismiss'); load(); }
  };

  if (!data?.alerts?.length) return null;
  const cells = data.capped_cells || 0;

  return (
    <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50/70 p-4" data-testid="queenless-alerts">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="font-bold text-rose-800 flex items-center gap-2">
          <Crown className="h-5 w-5" /> {data.alerts.length} hive{data.alerts.length === 1 ? '' : 's'} may be queenless
        </h3>
        <span className="text-xs font-medium text-stone-600" data-testid="queenless-capped-count">
          {cells ? `${cells} capped cell${cells === 1 ? '' : 's'} ready in Queen Rearing` : 'No capped cells ready right now'}
        </span>
      </div>
      <ul className="space-y-2">
        {data.alerts.map(a => (
          <li key={a.hive_id} className="flex flex-wrap items-center gap-2 rounded-xl bg-white border border-rose-100 px-3 py-2" data-testid={`queenless-alert-${a.hive_id}`}>
            <button onClick={() => navigate(`/hive/${a.hive_id}`)} className="text-left flex-1 min-w-[10rem]" data-testid={`queenless-alert-hive-${a.hive_id}`}>
              <span className="font-semibold text-stone-800">Hive {a.hive_number}{a.nickname ? ` "${a.nickname}"` : ''}</span>
              <span className="block text-xs text-stone-500">{a.yard} · {a.reason} · inspected {fmt(a.inspection_date)}</span>
            </button>
            <button onClick={() => navigate(`/?view=queen-rearing&transfer_to_hive=${a.hive_id}`)} disabled={!cells}
              className="inline-flex items-center gap-1 text-xs font-semibold rounded-full bg-indigo-600 text-white px-3 py-1.5 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`queenless-use-cell-${a.hive_id}`}>
              Use a capped cell <ArrowRight className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => dismiss(a)} className="p-1.5 rounded-full text-stone-400 hover:text-stone-700 hover:bg-stone-100" aria-label="Dismiss" data-testid={`queenless-dismiss-${a.hive_id}`}>
              <X className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default QueenlessAlerts;
